import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Settings, Activity, Zap, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface VehicleRow {
  id: string;
  code: string;
  plate_en: string | null;
  vehicle_type: string;
  auto_detection_enabled: boolean;
  pending_count: number;
  confirmed_count: number;
  patterns_count: number;
}

interface Tuning {
  STOP_RADIUS_M: number;
  STOP_MIN_DURATION_MIN: number;
  STOP_REST_THRESHOLD_MIN: number;
  TRIP_MIN_DISTANCE_KM: number;
  OPERATIONAL_HOUR_START: number;
  OPERATIONAL_HOUR_END: number;
  SITE_MATCH_RADIUS_M: number;
  FUEL_STATION_MATCH_RADIUS_M: number;
  DETECTION_CRON_INTERVAL_MIN: number;
  PATTERN_LEARNING_LOOKBACK_DAYS: number;
  PATTERN_MIN_OCCURRENCES: number;
}

const TYPE_LABELS: Record<string, string> = {
  water_tanker: 'Water tanker',
  sewage_tanker: 'Sewage tanker',
  dumper: 'Dumper',
  container_truck: 'Container truck',
  flatbed: 'Flatbed',
  pickup: 'Pickup',
  bus: 'Bus',
  other: 'Other',
};

export default function TripDetectionSettingsPage() {
  const [tuning, setTuning] = useState<Tuning | null>(null);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [running, setRunning] = useState<'detect' | 'learn' | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<{ tuning: Tuning; vehicles: VehicleRow[] }>('/api/v1/trip-detection/settings');
      setTuning(r.data.tuning);
      setVehicles(r.data.vehicles);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function toggle(v: VehicleRow) {
    setBusyId(v.id);
    try {
      await apiClient.patch('/api/v1/trip-detection/settings/vehicles/' + v.id + '/toggle', { enabled: !v.auto_detection_enabled });
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setBusyId(null); }
  }

  async function runDetectNow() {
    if (!window.confirm('Run trip detection now on all enabled vehicles?')) return;
    setRunning('detect');
    try {
      const r = await apiClient.post<{ vehicles_analyzed: number; candidates_detected: number }>('/api/v1/trip-detection/run-now', { lookbackHours: 24 });
      window.alert('Detection complete. Vehicles analyzed: ' + r.data.vehicles_analyzed + ', candidates: ' + r.data.candidates_detected);
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setRunning(null); }
  }

  async function runLearnNow() {
    if (!window.confirm('Run pattern learning now on all enabled vehicles? Requires confirmed/merged trips in the last 14 days.')) return;
    setRunning('learn');
    try {
      const r = await apiClient.post<{ vehicles: number; patterns: number }>('/api/v1/trip-detection/learn-now', { lookbackDays: 14 });
      window.alert('Pattern learning complete. Vehicles: ' + r.data.vehicles + ', patterns upserted: ' + r.data.patterns);
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setRunning(null); }
  }

  const enabledCount = vehicles.filter(v => v.auto_detection_enabled).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Settings className="h-4 w-4" /> Trip detection settings</div>
          <Link to="/dashboard/trips/auto-detected"><Button size="sm" variant="outline" className="gap-2"><Activity className="h-4 w-4" /> View detected</Button></Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : (
          <>
            <div className="rounded-lg border border-border p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Cron status</div>
              <div className="text-sm">Detection runs every <strong>{tuning?.DETECTION_CRON_INTERVAL_MIN ?? '?'}  minutes</strong>. Pattern learning runs nightly at <strong>02:00 Riyadh time</strong>.</div>
              <div className="text-sm text-muted-foreground">{enabledCount} of {vehicles.length} active vehicles have auto-detection enabled.</div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={runDetectNow} disabled={running !== null} className="gap-2">
                  {running === 'detect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Run detection now
                </Button>
                <Button size="sm" variant="outline" onClick={runLearnNow} disabled={running !== null} className="gap-2">
                  {running === 'learn' ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />} Run pattern learning now
                </Button>
              </div>
            </div>

            {tuning && (
              <details className="rounded-lg border border-border" open>
                <summary className="cursor-pointer px-4 py-2 text-sm font-medium bg-muted/30">
                  Global tuning constants <span className="text-xs text-muted-foreground">(read-only — change via deployment)</span>
                </summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 text-sm">
                  <Constant label="Stop radius" value={tuning.STOP_RADIUS_M + ' m'} />
                  <Constant label="Stop min duration" value={tuning.STOP_MIN_DURATION_MIN + ' min'} />
                  <Constant label="Rest threshold" value={tuning.STOP_REST_THRESHOLD_MIN + ' min'} />
                  <Constant label="Trip min distance" value={tuning.TRIP_MIN_DISTANCE_KM + ' km'} />
                  <Constant label="Site match radius" value={tuning.SITE_MATCH_RADIUS_M + ' m'} />
                  <Constant label="Fuel-station match radius" value={tuning.FUEL_STATION_MATCH_RADIUS_M + ' m'} />
                  <Constant label="Operational hours" value={tuning.OPERATIONAL_HOUR_START + ':00 – ' + tuning.OPERATIONAL_HOUR_END + ':00 (Riyadh)'} />
                  <Constant label="Pattern lookback" value={tuning.PATTERN_LEARNING_LOOKBACK_DAYS + ' days'} />
                  <Constant label="Pattern min occurrences" value={String(tuning.PATTERN_MIN_OCCURRENCES)} />
                </div>
              </details>
            )}

            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-2 text-sm font-medium bg-muted/30 border-b border-border">
                Per-vehicle auto-detection ({vehicles.length} active)
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs text-muted-foreground bg-muted/20 border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2">Code</th>
                      <th className="text-left px-3 py-2">Plate</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Pending</th>
                      <th className="text-right px-3 py-2">Confirmed</th>
                      <th className="text-right px-3 py-2">Patterns</th>
                      <th className="text-center px-3 py-2">Auto-detect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v) => (
                      <tr key={v.id} className="border-b border-border last:border-b-0 hover:bg-muted/10">
                        <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                        <td className="px-3 py-2 text-xs">{v.plate_en ?? '—'}</td>
                        <td className="px-3 py-2 text-xs">{TYPE_LABELS[v.vehicle_type] ?? v.vehicle_type}</td>
                        <td className="px-3 py-2 text-right">{v.pending_count}</td>
                        <td className="px-3 py-2 text-right">{v.confirmed_count}</td>
                        <td className="px-3 py-2 text-right">{v.patterns_count}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggle(v)}
                            disabled={busyId === v.id}
                            className={'inline-flex items-center px-3 py-1 rounded text-[10px] uppercase tracking-wider ' + (
                              v.auto_detection_enabled
                                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40'
                                : 'bg-stone-500/15 text-stone-700 dark:text-stone-400 border border-stone-500/40 hover:bg-emerald-500/15'
                            )}
                          >
                            {busyId === v.id ? '...' : (v.auto_detection_enabled ? 'ON' : 'OFF')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Constant({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/30 rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-sm mt-0.5">{value}</div>
    </div>
  );
}
